import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';

interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
  label: string;
  icon?: React.ReactNode;
  error?: string;
  rightElement?: React.ReactNode;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ label, icon, error, rightElement, disabled, readOnly, onFocus, onBlur, className, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const generatedId = React.useId();
    const inputId = props.id ?? generatedId;

    const rawValue = props.value ?? props.defaultValue;
    const hasValue = rawValue !== undefined && rawValue !== '' && rawValue !== null;
    // Inputs `date`/`datetime-local`/`time`/`month`/`week` sempre renderizam
    // um placeholder próprio do browser (ex.: "dd/mm/aaaa") — sem forçar
    // o label a flutuar, ele fica sobreposto ao placeholder quando vazio.
    const isNativePlaceholderType = props.type === 'date' || props.type === 'datetime-local'
      || props.type === 'time' || props.type === 'month' || props.type === 'week';
    const isFloated = isFocused || hasValue || isNativePlaceholderType;

    const borderColor = error
      ? 'border-red-500'
      : isFocused
      ? 'border-blue-500'
      : 'border-gray-300';

    return (
      <div className="w-full">
        <div className={`relative h-14 ${disabled ? 'opacity-60' : ''}`}>
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {icon}
            </div>
          )}

          <input
            ref={ref}
            {...props}
            id={inputId}
            placeholder=""
            disabled={disabled}
            readOnly={readOnly}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${inputId}-error` : undefined}
            onFocus={(e) => { setIsFocused(true); onFocus?.(e); }}
            onBlur={(e) => { setIsFocused(false); onBlur?.(e); }}
            className={[
              'peer w-full h-full rounded-lg border bg-white',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0',
              icon ? 'pl-10' : 'pl-4',
              rightElement ? 'pr-10' : 'pr-4',
              borderColor,
              disabled ? 'bg-gray-50 cursor-not-allowed' : '',
              readOnly ? 'bg-gray-50 cursor-default select-none' : '',
              className ?? '',
            ].join(' ')}
          />

          <label
            htmlFor={inputId}
            className={[
              'absolute pointer-events-none transition-all duration-200 select-none',
              isFloated
                ? `left-3 -top-2.5 text-xs bg-white px-1 font-medium ${
                    error ? 'text-red-500' : isFocused ? 'text-blue-600' : 'text-gray-500'
                  }`
                : `${icon ? 'left-10' : 'left-4'} top-1/2 -translate-y-1/2 text-sm text-gray-400`,
            ].join(' ')}
          >
            {label}
          </label>

          {rightElement && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {rightElement}
            </div>
          )}
        </div>

        {error && (
          <p
            id={`${inputId}-error`}
            role="alert"
            className="mt-1 text-sm text-red-600 flex items-center gap-1"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </p>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';
